import FutureMedicalCard from "components/Card/FutureMedicalCard";
import LibertyMutualReferralFields from "components/LibertyMutualReferralFields";
import EditServiceAssignees from "pages/files/sections/EditServiceAssignees";
import { useEffect, useMemo, useRef, useState } from "react";
import type { DefaultValues } from "react-hook-form";
import { useForm } from "react-hook-form";
import { useLocation } from "react-router-dom";
import {
  useGetFileByIdQuery,
  usePatchFileByIdMutation,
} from "services/file-api";
import { useGetTreatmentAllocationQuery } from "services/msa-api";
import type { CostProjectionDataType, MSADataType } from "types/DataTypes";
import { FEATURE_FLAGS } from "constants/FeatureFlags";
import { useConstants } from "utils/hooks";
import { useSelector } from "utils/redux";

import AmendedReviewOverviewCard from "../../amended-review-screening/AmendedReviewOverview.tsx";
import { getServiceField, isLibertyMutualFile } from "../future-medical-utils";
import AnnuityFundingCard from "./cards/AnnuityFundingCard";
import MedicalTreatmentCard from "./cards/MedicalTreatmentCard";
import PrescriptionAllocationCard from "./cards/PrescriptionAllocationCard";
import TreatmentAllocationCard from "./cards/TreatmentAllocationCard";
import type { AllocationFilterValue } from "./components/AllocationFilter/AllocationFilter";
import { AllocationFilter } from "./components/AllocationFilter/AllocationFilter";
import { AllocationUpdateProvider } from "./context/AllocationUpdateContext";
import type { TreatmentAllocationRowProps } from "./utils/allocation-types.ts";

type LibertyMutualFormValues = {
  grs_or_legacy_rcg: string;
  segmentation_type: string;
  rcg_carrier: string;
  referral_type: string;
  wcrc_case_id: string;
  beneficiary_class: string;
};

interface AllocationReviewProps {
  service: MSADataType | CostProjectionDataType;
  onSave: (field: string, value: unknown) => Promise<void>;
  canEditAssignee?: boolean;
  isComplete?: boolean;
  reviewType: "MEDICAL" | "LEGAL";
}

export const AllocationReview = ({
  service,
  onSave,
  canEditAssignee = false,
  reviewType,
}: AllocationReviewProps) => {
  const location = useLocation();
  const isAmendedReviewPage = location.pathname.includes(
    "/future-medical/amended-review",
  );
  const loggedInUser = useSelector((state) => state.main.loggedInUser);
  const isReadOnly = !loggedInUser?.has_operations_authority;

  const serviceId = service.id.toString();
  const [allocationFilter, setAllocationFilter] =
    useState<AllocationFilterValue>("ALL");

  const { data, isLoading } = useGetTreatmentAllocationQuery(
    {
      filters: getServiceField(service, true, reviewType),
    },
    { skip: !serviceId },
  );

  const allRows = data?.results || [];

  // filter needs to match full name, aka type_verbose
  const fileServices = service.file?.services?.filter(
    (s) => s.type_verbose === service.type,
  );

  // Liberty Mutual referral fields -- File-level, not service-level, so they
  // need their own fetch/mutation separate from `onSave` (which is scoped to
  // the MSA/CostProjection service). Only shown on the Medical Review tab.
  const fileId = service.file?.id;
  const { data: fileData } = useGetFileByIdQuery(
    { id: fileId?.toString() || "" },
    { skip: !fileId },
  );
  const [patchFileById] = usePatchFileByIdMutation();
  const { constants } = useConstants();
  const isLibertyFeatureEnabled = Boolean(
    constants?.FEATURE_FLAGS?.[FEATURE_FLAGS.LIBERTY_REFERRAL_FIELDS_ENABLED],
  );
  const isLiberty =
    isLibertyFeatureEnabled &&
    reviewType === "MEDICAL" &&
    isLibertyMutualFile(fileData);

  const {
    control: libertyControl,
    reset: resetLiberty,
    formState: { errors: libertyErrors },
  } = useForm<LibertyMutualFormValues>();

  useEffect(() => {
    if (!fileData) return;
    resetLiberty({
      grs_or_legacy_rcg: fileData.grs_or_legacy_rcg || "",
      segmentation_type: fileData.segmentation_type || "",
      rcg_carrier: fileData.rcg_carrier || "",
      referral_type: fileData.referral_type || "",
      wcrc_case_id: fileData.wcrc_case_id || "",
      beneficiary_class: fileData.beneficiary_class || "",
    });
  }, [fileData, resetLiberty]);

  const handleFileSave = async (field: string, value: unknown) => {
    if (!fileId) return;
    try {
      await patchFileById({
        id: fileId.toString(),
        data: { [field]: value },
      }).unwrap();
    } catch (error) {
      console.error("Error saving file field:", error);
    }
  };

  const defaultValues: DefaultValues<MSADataType | CostProjectionDataType> = {
    ...service,
  };

  const { control, reset, formState } = useForm<
    MSADataType | CostProjectionDataType
  >({
    defaultValues,
  });

  const prevServiceIdRef = useRef(service.id);

  useEffect(() => {
    const isSwitchingServices = prevServiceIdRef.current !== service.id;
    prevServiceIdRef.current = service.id;

    reset(
      {
        ...service,
      },
      {
        keepDirtyValues: !isSwitchingServices,
      },
    );
  }, [service, reset, formState.dirtyFields]);

  const handleFieldChange = async (field: string, value: unknown) => {
    await onSave(field, value);
  };

  const treatmentCard = useMemo(
    () => (
      <TreatmentAllocationCard
        allRows={allRows as unknown as TreatmentAllocationRowProps[]}
        isLoadingAllRows={isLoading}
        service={service}
        allocationFilter={allocationFilter}
        reviewType={reviewType}
      />
    ),
    [allRows, isLoading, service, allocationFilter, reviewType],
  );

  return (
    <AllocationUpdateProvider>
      <div className="swim-column">
        {isAmendedReviewPage && (
          <AmendedReviewOverviewCard
            service={service as MSADataType}
            onSave={onSave}
          />
        )}
        <AllocationFilter
          value={allocationFilter}
          onChange={setAllocationFilter}
        />
        {treatmentCard}
        <PrescriptionAllocationCard
          isLoadingAllRows={isLoading}
          service={service}
          allocationFilter={allocationFilter}
          reviewType={reviewType}
        />
        <MedicalTreatmentCard
          allRows={
            allRows as unknown as {
              legal_total_proposed_amount: number;
              legal_proposed_wcmsa_amount: number;
              legal_proposed_rx_amount: number;
              medical_total_proposed_amount: number;
              medical_proposed_wcmsa_amount: number;
              medical_proposed_rx_amount: number;
              is_excluded: boolean;
            }[]
          }
          service={service}
          control={control}
          handleFieldChange={handleFieldChange}
          reviewType={reviewType}
          errors={formState.errors}
        />
        <AnnuityFundingCard
          service={service}
          onSave={onSave}
          reviewType={reviewType}
        />
        {isLiberty && (
          <FutureMedicalCard heading="Liberty Mutual Referral Information">
            <LibertyMutualReferralFields
              control={libertyControl}
              errors={libertyErrors}
              readOnly={isReadOnly}
              showBeneficiaryClass
              onFieldSave={handleFileSave}
            />
          </FutureMedicalCard>
        )}
        {canEditAssignee && (
          <EditServiceAssignees fileServices={fileServices} />
        )}
      </div>
    </AllocationUpdateProvider>
  );
};

export const MedicalReview = (
  props: Omit<AllocationReviewProps, "canEditAssignee" | "reviewType">,
) => <AllocationReview {...props} reviewType="MEDICAL" />;

export const LegalReview = (
  props: Omit<AllocationReviewProps, "canEditAssignee" | "reviewType">,
) => <AllocationReview {...props} canEditAssignee reviewType="LEGAL" />;
